import React, { useState, useMemo } from 'react';
import './DeviceListTable.css';

const DeviceListTable = ({ devices, loading }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

    const getRowClassName = (device) => {
        if (device.status === 'online' && device.uniqueId !== device.name) {
            return 'row-online';
        } else if (device.status === 'unknown') {
            return 'row-unknown';
        } else {
            return 'row-offline';
        }
    };

    const formatDeviceTime = (lastUpdate) => {
        if (!lastUpdate) return 'N/A';
        const date = new Date(lastUpdate);
        return date.toLocaleString();
    };

    const getPowerValue = (device) => {
        if (device.PositionData && device.PositionData.length > 0) {
            const powerValue = device.PositionData[0]?.attributes?.power;
            if (typeof powerValue === 'number') {
                return powerValue.toFixed(2);
            }
        }
        return 'N/A';
    };

    const getDeviceNumber = (name) => {
        if (!name) return '';
        const parts = name.split(' - ');
        return parts.length > 1 ? parts[0] : '';
    };

    // Sorting Logic
    const sortedDevices = useMemo(() => {
        if (!devices) return [];
        let sortableItems = [...devices];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue, bValue;

                // Determine values based on sort key
                switch (sortConfig.key) {
                    case 'number':
                        aValue = getDeviceNumber(a.name);
                        bValue = getDeviceNumber(b.name);
                        // Try numeric sort for Number column
                        const aNum = parseFloat(aValue);
                        const bNum = parseFloat(bValue);
                        if (!isNaN(aNum) && !isNaN(bNum)) {
                            aValue = aNum;
                            bValue = bNum;
                        }
                        break;
                    case 'power':
                        aValue = parseFloat(getPowerValue(a)) || 0;
                        bValue = parseFloat(getPowerValue(b)) || 0;
                        break;
                    case 'lastUpdate':
                        aValue = new Date(a.lastUpdate || 0).getTime();
                        bValue = new Date(b.lastUpdate || 0).getTime();
                        break;
                    default:
                        aValue = a[sortConfig.key];
                        bValue = b[sortConfig.key];
                        // Handle null/undefined
                        if (aValue === undefined || aValue === null) aValue = '';
                        if (bValue === undefined || bValue === null) bValue = '';
                        // Case insensitive string sort
                        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
                        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [devices, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (name) => {
        if (sortConfig.key !== name) {
            return <span className="material-icons-outlined sort-icon">sort</span>;
        }
        return (
            <span className={`material-icons-outlined sort-icon active`}>
                {sortConfig.direction === 'ascending' ? 'arrow_upward' : 'arrow_downward'}
            </span>
        );
    };

    if (loading) {
        return (
            <div className="device-table-loading">
                <div className="loading-spinner"></div>
                <p>Loading devices...</p>
            </div>
        );
    }

    return (
        <div className="device-table-wrapper">
            <div className="device-table-container">
                <table className="device-table">
                    <thead>
                        <tr>
                            <th onClick={() => requestSort('number')}>
                                <div className="sort-header">Number {getSortIcon('number')}</div>
                            </th>
                            <th onClick={() => requestSort('id')}>
                                <div className="sort-header">ID {getSortIcon('id')}</div>
                            </th>
                            <th onClick={() => requestSort('name')}>
                                <div className="sort-header">Name {getSortIcon('name')}</div>
                            </th>
                            <th onClick={() => requestSort('uniqueId')}>
                                <div className="sort-header">Unique ID (IMEI) {getSortIcon('uniqueId')}</div>
                            </th>
                            <th onClick={() => requestSort('status')}>
                                <div className="sort-header">Status {getSortIcon('status')}</div>
                            </th>
                            <th onClick={() => requestSort('power')}>
                                <div className="sort-header">Power {getSortIcon('power')}</div>
                            </th>
                            <th onClick={() => requestSort('lastUpdate')}>
                                <div className="sort-header">DeviceTime {getSortIcon('lastUpdate')}</div>
                            </th>
                            <th onClick={() => requestSort('phone')}>
                                <div className="sort-header">Phone {getSortIcon('phone')}</div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedDevices && sortedDevices.length > 0 ? (
                            sortedDevices.map(device => (
                                <tr key={device.id} className={getRowClassName(device)}>
                                    <td className="device-number">{getDeviceNumber(device.name)}</td>
                                    <td>{device.id}</td>
                                    <td className="device-name">{device.name}</td>
                                    <td>{device.uniqueId}</td>
                                    <td>
                                        <span className={`status-badge status-${device.status}`}>
                                            {device.status}
                                        </span>
                                    </td>
                                    <td>{getPowerValue(device)}</td>
                                    <td>{formatDeviceTime(device.lastUpdate)}</td>
                                    <td>{device.phone || ''}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className="no-devices">
                                    No devices found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default DeviceListTable;
